// Inbox › Calls in Blueprint (calls round; the approved page story, Pages/Inbox/Calls), on today's data:
// the calls as CallStrip lists of CallRows, so their columns line up. On calls now (today's calls
// answered on a phone and still running: who, their number, who has it, how long), To call back
// (calls.callbacks, oldest first: each one's kind and time, what happened, the line it came in on, the
// number, and one action), and Earlier today (today's other calls, the newest first: how each went,
// who took it and how long; Show all for the rest). Ringing calls aren't listed: the strip under the
// top bar has them, and holds the page's filled button while one rings; otherwise the first Call back
// does. A caller's name opens the call's page. Words: calls-lists-model.ts.
import { useEffect, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { CallAction, CallRow, CallStrip, Link, StateMark } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { useMirror } from '../shared/mirror.ts';
import { phoneText, type Call } from '../types.ts';
import { lookBackWord, olderThan, type Callback } from './callbacks-model.ts';
import {
  backAction,
  backName,
  backNumber,
  backState,
  backWhat,
  EARLIER_SHOWN,
  earlierState,
  earlierWho,
  lineOf,
  listName,
  whoTook,
} from './calls-lists-model.ts';
import { callWrite } from './reasons-model.ts';
import { stillRinging } from './ringing-model.ts';
import { sinceWords } from './strip-model.ts';

export function CallsLists({
  today,
  loading,
  backs,
  whenOf,
  canClaim,
  onOpen,
  onOlder,
  onBacksChanged,
  side,
}: {
  /** Today's calls, newest first; null while they load. */
  today: Call[] | null;
  /** While today's calls load: its line (and Try again). */
  loading: ReactNode;
  backs: { calls: Callback[] | null; days: number; failed: boolean };
  /** "2:14 p.m." today, "Oct 15, 2:14 p.m." before. */
  whenOf: (iso: string) => string;
  /** They may edit calls (customers.edit): Call back makes the call theirs first, and a call handled
   * on a later one can be said so. */
  canClaim: boolean;
  onOpen: (callId: string) => void;
  onOlder: (days: number) => void;
  onBacksChanged: () => void;
  /** Under Earlier today (recordings to match, for owners and managers). */
  side?: ReactNode;
}) {
  const mirroring = useMirror();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const onCalls = (today ?? []).filter((c) => c.status === 'in_progress');
  // the clock on each live call, while there are any
  const live = onCalls.length > 0;
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);

  const write = async (callId: string, patch: Record<string, unknown>) => {
    setBusy(callId);
    setError(null);
    try {
      await client.call(callWrite(mirroring), { id: callId, ...patch });
      onBacksChanged();
      return true;
    } catch (e) {
      setError(e instanceof RpcError ? e.message : 'That didn’t save. Try again.');
      onBacksChanged();
      return false;
    } finally {
      setBusy(null);
    }
  };
  // Call back: nobody has it, so it's theirs first ("I'll call them back"), then its page to call from
  const callBack = async (b: Callback) => {
    if (!b.takenBy && canClaim && !(await write(b.callId, { claim: true }))) return;
    onOpen(b.callId);
  };

  const waiting = new Set((backs.calls ?? []).map((b) => b.callId));
  const earlier = (today ?? []).filter(
    (c) => c.status !== 'in_progress' && !stillRinging(c, now) && !waiting.has(c.id),
  );
  const shown = all ? earlier : earlier.slice(0, EARLIER_SHOWN);
  // the strip holds the filled button while a call rings; else the first call to return does
  const ringing = (today ?? []).some((c) => stillRinging(c, now));
  const primary = ringing ? null : (backs.calls ?? []).find((b) => backAction(b) === 'call-back')?.callId;
  const older = olderThan(backs.days);
  const opens = (id: string, name: string) => <Link onPress={() => onOpen(id)}>{name}</Link>;
  return (
    <div className="calls-bp">
      <div className="calls-bp-col">
        {live && (
          <CallStrip variant="list" label="On calls now" count={onCalls.length}>
            {onCalls.map((c) => {
              const by = whoTook(c);
              const line = lineOf(c);
              return (
                <CallRow
                  key={c.id}
                  kind="other"
                  state="On a call"
                  name={opens(c.id, listName(c))}
                  who={by ? `${phoneText(c.from)}, with ${by}` : phoneText(c.from)}
                  {...(line ? { line } : {})}
                  time={sinceWords(c.startedAt, now)}
                />
              );
            })}
          </CallStrip>
        )}
        <CallStrip
          variant="list"
          label="To call back"
          count={backs.calls?.length ?? 0}
          countHot={!!backs.calls?.length}
          foot={
            backs.failed ? (
              'Couldn’t read the calls to call back.'
            ) : !backs.calls ? (
              'Reading the calls…'
            ) : !backs.calls.length ? (
              'Nobody’s waiting on a call back.'
            ) : older ? (
              <>
                {lookBackWord(backs.days)}, oldest first.{' '}
                <Link onPress={() => onOlder(older)}>Show older</Link>
              </>
            ) : (
              `${lookBackWord(backs.days)}, oldest first.`
            )
          }
        >
          {(backs.calls ?? []).map((b) => {
            const name = backName(b);
            const line = lineOf(b);
            // one who may not edit calls opens it rather than say it was handled (QA-773)
            const act = backAction(b) === 'handled' && !canClaim ? 'open' : backAction(b);
            return (
              <CallRow
                key={b.callId}
                kind={b.why === 'missed' ? 'missed' : 'callback'}
                state={backState(b, whenOf(b.startedAt))}
                name={opens(b.callId, name)}
                who={backWhat(b, whenOf)}
                {...(line ? { line } : {})}
                time={backNumber(b)}
                actions={
                  act === 'call-back' ? (
                    <CallAction
                      variant={b.callId === primary ? 'primary' : 'link'}
                      aria-label={`Call back ${name}`}
                      isDisabled={busy === b.callId}
                      onPress={() => void callBack(b)}
                    >
                      Call back
                    </CallAction>
                  ) : act === 'handled' ? (
                    <CallAction
                      isDisabled={busy === b.callId}
                      onPress={() => void write(b.callId, { handledOnCallId: b.laterCall!.callId })}
                    >
                      Handled on their later call
                    </CallAction>
                  ) : (
                    <CallAction aria-label={`Open the call from ${name}`} onPress={() => onOpen(b.callId)}>
                      Open the call
                    </CallAction>
                  )
                }
              />
            );
          })}
        </CallStrip>
        {error && (
          <p className="calls-bp-bad" role="alert">
            <StateMark state="problem" />
            {error}
          </p>
        )}
      </div>
      <div className="calls-bp-col">
        <CallStrip
          variant="list"
          label="Earlier today"
          count={earlier.length}
          foot={
            today === null ? undefined : !earlier.length ? (
              today.length ? (
                'Nothing else yet today.'
              ) : (
                'No calls yet today. When the phone rings, the caller shows on the strip before anyone answers.'
              )
            ) : shown.length < earlier.length ? (
              <Link onPress={() => setAll(true)}>Show all {earlier.length}</Link>
            ) : undefined
          }
        >
          {shown.map((c) => {
            const { kind, state } = earlierState(c);
            const who = earlierWho(c);
            const line = lineOf(c);
            return (
              <CallRow
                key={c.id}
                kind={kind}
                state={state}
                name={opens(c.id, listName(c))}
                {...(who ? { who } : {})}
                {...(line ? { line } : {})}
                time={whenOf(c.startedAt)}
              />
            );
          })}
        </CallStrip>
        {today === null && loading}
        {side}
      </div>
    </div>
  );
}
